import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Quote } from "lucide-react";
import { api } from "@/lib/api";
import { TabKicker, TabSectionTitle } from "./_shell";

/**
 * TabOutcomesExtras — editorial employer strip.
 * Chips are sharp, mono-labeled, and hover-tinted with accent purple.
 *
 * Testids preserved: tab-outcomes-top-employers, outcomes-employer-chip-*
 */

export default function TabOutcomesExtras({ uni }) {
  const [data, setData] = useState(null);
  useEffect(() => {
    let cancelled = false;
    api.get(`/universities/${uni.id}/outcome-signals`)
      .then((r) => { if (!cancelled) setData(r.data); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [uni.id]);

  if (!data?.top_employers?.length) return null;
  const total = data.top_employers.reduce((s, e) => s + (e.n_courses || 0), 0);
  const top = data.top_employers[0];

  return (
    <section className="mb-8" data-testid="tab-outcomes-top-employers">
      <div>
        <TabKicker>01 · Destination Firms</TabKicker>
        <TabSectionTitle>Where {uni.name} grads actually land</TabSectionTitle>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-12 gap-6 md:gap-8">
        <div className="md:col-span-4 flex flex-col p-6 md:p-8 border-t-4 border-[#002147] bg-[#F3F1EC]">
          <span className="font-mono text-xs uppercase tracking-[0.2em] text-[#7C3AED] mb-2 block">
            Most-frequent employer
          </span>
          <Link
            to={`/how-to-get-into/${top?.slug}`}
            data-testid={`outcomes-top-employer-link-${top?.slug}`}
            className="font-serif text-3xl md:text-4xl text-[#002147] leading-tight mb-2 hover:text-[#7C3AED] hover:underline decoration-[#7C3AED]/40 decoration-2 underline-offset-4 transition-colors"
          >
            {top?.employer}
          </Link>
          <div className="font-mono text-sm uppercase tracking-widest text-[#002147]/60">
            {top?.n_courses} feeder course{top?.n_courses === 1 ? "" : "s"}
          </div>
          <div className="mt-4 font-sans text-sm text-[#002147]/80 border-t border-[#002147]/10 pt-4 leading-relaxed">
            The single firm reported most often by graduates across the university's undergraduate courses.
            Aggregated over {total} sighting{total === 1 ? "" : "s"}.
          </div>
          <Link
            to={`/how-to-get-into/${top?.slug}`}
            data-testid={`outcomes-top-employer-cta-${top?.slug}`}
            className="mt-4 inline-flex items-center gap-1 self-start font-mono text-[10px] uppercase tracking-widest text-[#7C3AED] hover:underline"
          >
            See how to get in <ArrowRight className="h-3 w-3" />
          </Link>
        </div>

        <div className="md:col-span-8 p-6 bg-white/50 border border-[#002147]/10 relative overflow-hidden">
          <div className="flex justify-between items-baseline mb-6 border-b border-[#002147]/10 pb-4">
            <h3 className="font-sans font-semibold text-lg text-[#002147]">
              Top 10 employers
            </h3>
            <span className="font-mono text-[10px] text-[#002147]/60 uppercase tracking-widest">
              Tap for pay bands
            </span>
          </div>
          <div className="flex flex-wrap gap-2">
            {data.top_employers.map((e) => (
              <Link
                key={e.slug}
                to={`/how-to-get-into/${e.slug}`}
                data-testid={`outcomes-employer-chip-${e.slug}`}
                className="inline-flex items-center gap-2 px-3 py-1.5 bg-[#F3F1EC] border border-[#002147]/10 hover:border-[#7C3AED] hover:bg-[#7C3AED]/[0.04] transition-colors font-sans text-sm rounded-full group"
              >
                <span className="text-[#002147]">{e.employer}</span>
                <span className="font-mono text-[10px] uppercase tracking-widest text-[#002147]/40 group-hover:text-[#7C3AED]/70">
                  {e.n_courses}
                </span>
                <ArrowRight className="h-3 w-3 text-[#002147]/30 group-hover:text-[#7C3AED] transition" />
              </Link>
            ))}
          </div>
          <p className="mt-4 font-mono text-[10px] uppercase tracking-wider text-[#002147]/50 border-t border-[#002147]/10 pt-4">
            Sector labels filtered out · firms only · aggregated across UG courses
          </p>
        </div>
      </div>

      {/* ── Supplementary source strips (honest depth split) ────── */}
      <AlumniPullQuote quote={data.alumni_pull_quote} />
      <SidecarStrip
        title="LinkedIn alumni · where they work"
        source="LINKEDIN GRAPH · GOOGLE-INDEXED"
        note="Aggregate signal drawn from public Google-indexed LinkedIn snippets. Not per-subject; not a per-cohort count. Use for direction, not precision."
        items={data.linkedin_alumni_employers || []}
        testidStem="outcomes-linkedin-chip"
      />
      <SidecarStrip
        title="Careers-office employer mentions"
        source="UNI CAREERS PAGE · SERP-MINED"
        note="Firms named in the university's own careers-office web pages (as indexed by Google). Uni-level aggregate, not per-degree."
        items={data.careers_page_aggregate_employers || []}
        testidStem="outcomes-careers-chip"
      />
    </section>
  );
}


function AlumniPullQuote({ quote }) {
  if (!quote?.quote || !quote?.alumnus_name) return null;
  // Compose the attribution line from whichever fields we have.
  const parts = [];
  if (quote.role) parts.push(quote.role);
  if (quote.employer) parts.push(quote.employer);
  const details = [quote.degree, quote.year].filter(Boolean).join(" · ");
  return (
    <figure
      className="mt-6 p-6 bg-[#7C3AED]/[0.04] border-l-4 border-[#7C3AED]"
      data-testid="outcomes-alumni-pull-quote"
    >
      <div className="flex gap-4">
        <Quote className="h-6 w-6 text-[#7C3AED]/60 flex-shrink-0 mt-1" />
        <div className="flex-1">
          <blockquote className="font-serif text-lg italic leading-snug text-[#002147]">
            &ldquo;{quote.quote}&rdquo;
          </blockquote>
          <figcaption className="mt-3 flex items-baseline flex-wrap gap-x-3 gap-y-1">
            <span className="font-sans font-semibold text-sm text-[#002147]">
              {quote.alumnus_name}
            </span>
            {details && (
              <span className="font-mono text-[11px] uppercase tracking-widest text-[#002147]/50">
                {details}
              </span>
            )}
            {parts.length > 0 && (
              <span className="font-sans text-xs text-[#002147]/60">
                · {parts.join(", ")}
              </span>
            )}
            {quote.source_url && (
              <a
                href={quote.source_url}
                target="_blank"
                rel="noreferrer"
                className="ml-auto font-mono text-[10px] uppercase tracking-widest text-[#7C3AED] hover:underline"
                data-testid="outcomes-alumni-pull-quote-source"
              >
                Source →
              </a>
            )}
          </figcaption>
        </div>
      </div>
    </figure>
  );
}


function SidecarStrip({ title, source, note, items, testidStem }) {
  if (!items || items.length < 3) return null;
  return (
    <div className="mt-6 p-6 bg-white/50 border border-[#7C3AED]/15" data-testid={`${testidStem}-strip`}>
      <div className="flex justify-between items-baseline mb-4 border-b border-[#7C3AED]/15 pb-3">
        <h4 className="font-sans font-semibold text-sm text-[#002147]">{title}</h4>
        <span className="font-mono text-[9px] uppercase tracking-widest text-[#7C3AED]">
          {source}
        </span>
      </div>
      <div className="flex flex-wrap gap-2">
        {items.map((e, i) => (
          <Link
            key={`${e.slug}-${i}`}
            to={`/how-to-get-into/${e.slug}`}
            data-testid={`${testidStem}-${e.slug}`}
            className="inline-flex items-center gap-2 px-3 py-1.5 bg-[#F3F1EC] border border-[#7C3AED]/20 hover:border-[#7C3AED] hover:bg-[#7C3AED]/[0.06] transition-colors font-sans text-sm rounded-full group"
          >
            <span className="text-[#002147]">{e.employer}</span>
            {e.count ? (
              <span className="font-mono text-[10px] uppercase tracking-widest text-[#002147]/40 group-hover:text-[#7C3AED]/70">
                {e.count.toLocaleString ? e.count.toLocaleString() : e.count}
              </span>
            ) : null}
            <ArrowRight className="h-3 w-3 text-[#002147]/30 group-hover:text-[#7C3AED] transition" />
          </Link>
        ))}
      </div>
      <p className="mt-3 font-mono text-[10px] uppercase tracking-wider text-[#002147]/50 border-t border-[#7C3AED]/10 pt-3">
        {note}
      </p>
    </div>
  );
}
